// src/components/modals/AddUnitModal.jsx
import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { X, Loader2, ChevronDown } from 'lucide-react'
import UnitsService, { typeOptions } from '../../services/unitsService'

const AddUnitModal = ({ isOpen, onClose, onUnitAdded }) => {
  const [formData, setFormData] = useState({
    name: '',
    abbreviation: '',
    type: '',
    description: ''
  })
  const [availableAbbreviations, setAvailableAbbreviations] = useState([])
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // Update available abbreviations when type changes
  useEffect(() => {
    if (formData.type) {
      const abbreviations = UnitsService.getAbbreviationsForType(formData.type)
      setAvailableAbbreviations(abbreviations)

      // Reset abbreviation if it's not valid for the new type
      if (formData.abbreviation && !abbreviations.some(abbr => abbr.value === formData.abbreviation)) {
        setFormData(prev => ({
          ...prev,
          abbreviation: ''
        }))
      }
    } else {
      setAvailableAbbreviations([])
    }
  }, [formData.type, formData.abbreviation])

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Handle dropdown changes
  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Clear error when user makes selection
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Unit name is required'
    }

    if (!formData.type) {
      newErrors.type = 'Type is required'
    }

    if (!formData.abbreviation) {
      newErrors.abbreviation = 'Abbreviation is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return

    setLoading(true)
    try {
      const unitData = {
        ...formData,
        isActive: true // New units are active by default
      }

      console.log('📏 Creating unit with data:', unitData)
      console.log('📏 Type value being sent:', unitData.type)
      console.log('📏 Available type options:', typeOptions)

      const response = await UnitsService.createUnit(unitData)

      if (response.success) {
        console.log('✅ Unit created successfully:', response.data.unit)
        onUnitAdded(response.data.unit)
        handleClose()
      } else {
        console.log('⚠️ Creation failed with response:', response)
        setErrors({ general: response.message || 'Failed to create unit' })
      }
    } catch (error) {
      console.error('❌ Error creating unit:', error)

      // Enhanced error handling
      let errorMessage = 'Failed to create unit. Please try again.'

      if (error.message.includes('duplicate') || error.message.includes('already exists')) {
        errorMessage = 'A unit with this name or abbreviation already exists.'
      } else if (error.message.includes('Unauthorized')) {
        errorMessage = 'You are not authorized to create units. Please login again.'
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      } else if (error.message.includes('must be one of')) {
        errorMessage = 'Invalid unit type selected. Please try again.'
      }

      setErrors({ general: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    setFormData({
      name: '',
      abbreviation: '',
      type: '',
      description: ''
    })
    setAvailableAbbreviations([])
    setErrors({})
    setLoading(false)
    onClose()
  }

  // Don't render if not open
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Add Unit</h3>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Error Message */}
          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{errors.general}</p>
            </div>
          )}

          {/* Form */}
          <div className="space-y-5">
            {/* Unit Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Unit name *
              </label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="eg. Kilogram"
                disabled={loading}
                className={errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            {/* Type Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type *
              </label>
              <div className="relative">
                <select
                  name="type"
                  value={formData.type}
                  onChange={(e) => {
                    console.log('🔍 Type selected:', e.target.value)
                    handleSelectChange('type', e.target.value)
                  }}
                  disabled={loading}
                  className={`w-full p-3 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed appearance-none ${errors.type ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-200'
                    }`}
                >
                  <option value="">Select type</option>
                  {typeOptions.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.type && (
                <p className="text-red-500 text-sm mt-1">{errors.type}</p>
              )}
            </div>

            {/* Abbreviation Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Abbreviation *
              </label>
              <div className="relative">
                <select
                  name="abbreviation"
                  value={formData.abbreviation}
                  onChange={(e) => handleSelectChange('abbreviation', e.target.value)}
                  disabled={loading || !formData.type}
                  className={`w-full p-3 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed appearance-none ${errors.abbreviation ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-200'
                    }`}
                >
                  <option value="">
                    {!formData.type ? 'Select type first' : 'Select abbreviation'}
                  </option>
                  {availableAbbreviations.map((abbr) => (
                    <option key={abbr.value} value={abbr.value}>
                      {abbr.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.abbreviation && (
                <p className="text-red-500 text-sm mt-1">{errors.abbreviation}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Unit description (optional)"
                disabled={loading}
                className="w-full p-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Status Toggle (Always Active for new units) */}
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <div className="flex items-center">
                <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-orange-500">
                  <span className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out translate-x-6" />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-6">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-black hover:bg-gray-800 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                'Create Unit'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddUnitModal